import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { IconButton } from "@chakra-ui/react";
import { ChartLegend, PopoverCard, Switcher } from "../../../Components/Atoms";
import { ModelPicker, ReactTable} from "../../../Components/Molecules";
import { Stack } from "../../../Components/Organisms/Graphs";
import { Report } from "../../../Templates";

import { getStatsData, getVehicleStats, getWidgetData } from "../../../APIs";
import { DATE_RANGES } from "../../../Library/constants";
import { MODULE_TYPES, processReportData, STATS_ENUMS } from "../../../Library/widgets";
import { StoreContext } from "../../../Library/contexts";
import { DownloadIcon } from "../../../Assets/images/svgs";
import PATHS from "../../../Routes/paths";
import { THRESHOLD_TABLE_HEADERS } from "../../../Library/tableHeaders";
import { getDateTimeRows, getUserAndVehicle } from "../../../Library/helpers";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";

const labelData = [ "1", "2", "3", "4", "5+" ];
const NORMAL_OPTION_IDX = 2;
const parser = { x: "model", y: "count", xLabel: "Vehicles Count", yLabel: "Times of Threshold Crossed" };

const paths = [
  { idx: 0, name: "Analytics", link: PATHS.ANALYTICS },
  { idx: 1, name: "Motor Temperature", isCurrentPage: true },
];

const processData = (data, constants) => {
  // For Grouping Counts >= 5
  const processed = [];
  const legends = [];

  data.forEach((d) => {
    let counts = [];

    d.count.forEach((c) => {
      let index = (c.idx >= 5) ? 4 : c.idx - 1;

      if (counts[index]) counts[index] += c.count;
      else counts[index] = c.count;
    });

    processed.push({
      color: constants.modelMap[d.model].color,
      model: constants.modelMap[d.model].nm,
      count: counts,
      modelId: d.model,
    });

    legends.push({ label: constants.modelMap[d.model].nm, color: constants.modelMap[d.model].color, modelId: d.model });
  });

  return { graphs: processed, legends };
};

const MotorTemperature = () => {
  const navigate = useNavigate();
  const { filters, constants } = useContext(StoreContext);

  const reportRef = useRef(null);
  const widgetData = useRef(null);
  const reportData = useRef({});
  const tableData = useRef({});

  const [ isLoading, setIsLoading ] = useState(true);
  const [ selected, setSelected ] = useState();
  const [ categories, setCategories ] = useState();
  const [ tableHeaders, setTableHeaders ] = useState(THRESHOLD_TABLE_HEADERS);
  const [ showOverlay, setShowOverlay ] = useState(false);

  const handleSelect = useCallback(async (option, filters, constants) => {
    if (!reportData.current[option.idx]) {
      const enums = STATS_ENUMS[MODULE_TYPES.MOTOR_TEMPERATURE];
      if (enums[option.idx].graph) {
        const query = { typ: option.idx };
        const response = await getStatsData(DATE_RANGES[filters.range], MODULE_TYPES.MOTOR_TEMPERATURE, query);
        const grouped = processReportData(response, "vehCnt");   // Grouped Model-wise
        reportData.current[option.idx] = processData(grouped, constants);
      }
    }

    if (!tableData.current[option.idx]) {
      const query = { typ: option.idx };
      const response = await getVehicleStats(DATE_RANGES[filters.range], MODULE_TYPES.MOTOR_TEMPERATURE, query);
      tableData.current[option.idx] = getUserAndVehicle(response);
    }

    setSelected(option);

    if (option.idx === NORMAL_OPTION_IDX) {
      let newHeaders = {...THRESHOLD_TABLE_HEADERS};
      delete newHeaders.faults;
      setTableHeaders(newHeaders);
    }
    else setTableHeaders(THRESHOLD_TABLE_HEADERS);
  }, []);

  const handleFetchData = useCallback(async (filters, constants) => {
    // 1. Fetch Widget Data if not already fetched.
    // 2. Fetch Graph Data if not already fetched for the type.

    setIsLoading(true);
    let switcherOptions = [];
    let categories = [];

    if (!widgetData.current) {
      // Used to Fetch Switcher Options
      const enums = STATS_ENUMS[MODULE_TYPES.MOTOR_TEMPERATURE];
      const widgetData = await getWidgetData(DATE_RANGES[filters.range], [MODULE_TYPES.MOTOR_TEMPERATURE]);
      if (!widgetData) return;

      categories = widgetData[0].data[0] || [];
      widgetData.current = categories;

      switcherOptions = [];
      Object.values(enums).forEach(async (option) => {
        let count = categories[option.idx] ? `(${categories[option.idx]})` : "";
        switcherOptions.push({
          idx: option.idx, label: `${enums[option.idx].label} ${count}`,
        });
      });

      let defaultSelected = switcherOptions[0];
      reportData.current = {};
      tableData.current = {};

      await handleSelect(defaultSelected, filters, constants);
      setCategories(switcherOptions);
    }

    setIsLoading(false);

  }, [ handleSelect ]);

  useEffect(() => {
    if (reportRef.current) reportRef.current.apply();
  }, []);


  const getFilteredData = (data) => {
    const modelSet = new Set(filters.models);
    return data.filter((d) => modelSet.has(d.modelId));
  };

  const handleClick = (row, col) => {
    if (col == undefined) return;

    const newPaths = [ ...paths ];
    newPaths[paths.length - 1] = {
      ...newPaths[paths.length - 1],
      link: PATHS.ANALYTICS + "/" + PATHS.MOTOR_TEMPERATURE,
      isCurrentPage: false,
    };
    newPaths.push({ idx: 2, name: selected.label, isCurrentPage: true });

    const graphData = getFilteredData(reportData.current[selected.idx].graphs);

    const state = {
      // model: graphData[col].modelId,
      fault: MODULE_TYPES.MOTOR_TEMPERATURE,
      title: `Motor Temperature - ${selected.label}`,
      paths: newPaths,
      typ: selected ? selected.idx : NORMAL_OPTION_IDX,
      hideThresholdCrossed: selected.idx === NORMAL_OPTION_IDX,
    };

    navigate(PATHS.REPORTS, { state });
  };

  const removePopover = (toggle) => {
    if (toggle) setShowOverlay((old) => !old);
    else setShowOverlay(false);
  };

  const columns = React.useMemo(() => Object.values(tableHeaders).map(header => {
    return {
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) =>(header.popoverCard && row.value && row.value.length) ? (
        <PopoverCard
          data={row.value.length}
          cardTitle="Dates of Threshold crossed"
          array={getDateTimeRows(row.value)}
          removePopover={removePopover}
          dateTime={true}
        />
      ) : (row.value || "-"),
    };
  }), [tableHeaders]);

  const downloadData = (data) => {
    if(!data.length) return;

    var sendToExcel = data.map(a => Object.assign({}, a));
    sendToExcel.forEach((vehicle, idx) => {
      vehicle["Sr No"] = idx + 1;
      vehicle["Vehicle ID"] = vehicle.idx;
      vehicle["Device ID"] = vehicle.did;
      vehicle["Vehicle Make"] = vehicle.vmk;
      vehicle["Vehicle Model"] = vehicle.mo;
      vehicle["City"] = vehicle.user.cName;
      vehicle["State"] = vehicle.user.sName;
      vehicle["User ID"] = vehicle.uid;
      vehicle["User Name"] = `${vehicle.user.fn} ${vehicle.user.ln}`;
      vehicle["User Mob"] = vehicle.user.mob;
      vehicle["User Email"] = vehicle.user.eml;
      vehicle["Times of Threshold Crossed"] = vehicle.stat.data;
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Vehicle ID, Device ID, Vehicle Make, Vehicle Model, City, State, Times of Threshold Crossed, User ID, User Name, User Mob, User Email";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Motor_Temprature_" + Date.now(), false);
  }

  return (
    <Report breadcrumb={paths} title={"Motor Temperature"} isLoading={isLoading} onApply={handleFetchData} ref={reportRef}>
      {!isLoading && (
        <>
          <div className="panel-chart-switch">
            <Switcher
              options={categories}
              selectedOption={selected.idx}
              handleChange={(option) => handleSelect(option, filters, constants)}
              className="panel-graph-switcher"
            />
          </div>

          {reportData.current[selected.idx] && (
            <>
              <div className="panel-chart-header">
                <ChartLegend data={getFilteredData(reportData.current[selected.idx].legends)} />
                <ModelPicker />
              </div>

              <div className="panel-chart-main">
                <label className="panel-chart-label">
                  Times of Threshold Crossed
                </label>

                <Stack data={getFilteredData(reportData.current[selected.idx].graphs)} labels={labelData} parser={parser} title="Threshold Crossed" onClick={handleClick} />
              </div>
            </>
          )}

          <div className="panel-chart-sheet">
            <div className="panel-chart-sheet-header">
              <IconButton
                className="panel-button"
                aria-label='Download Report'
                icon={<DownloadIcon />}
                onClick={() => downloadData(tableData.current[selected.idx])}
              />
            </div>

            <div className="panel-chart-table">
              <ReactTable
                columns={columns}
                data={tableData.current[selected.idx]}
                showSearch={true}
                showOverlay={showOverlay}
              />
            </div>
          </div>
        </>
      )}
    </Report>
  );
}

export default MotorTemperature;
